import {
  arrayToRowSelectionModel,
  rowSelectionModelToArray,
} from "portal/utils/gridRowSelection";
import { buildPermission } from "common/utils/auth";
import { CarbonLoadingSpinner } from "portal/components/CarbonLoadingSpinner";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { Field, useFormikContext } from "formik";
import { Select as FormikSelect, TextField } from "formik-mui";
import { getPlantingHeading } from "common/utils/farm";
import {
  MenuItem,
  Select as MuiSelect,
  Paper,
  Typography,
} from "@mui/material";
import { NewJobsFormState } from "./FormState";
import { NewJobsWizardFormProps } from "./NewJobsWizard";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { typedPath } from "typed-path";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { WizardStepButtonBar } from "./WizardStepButtonBar";
import { Zone } from "protos/farm/farm";
import React, { useMemo } from "react";

const S = typedPath<NewJobsFormState>();

interface ZoneModel {
  id: string;
  fieldName: string;
  farmName: string;
  pathOrigin: string | undefined;
  pathDestination: string | undefined;
  plantingHeading: number | undefined;
  zone: Zone;
}

interface ZoneSelectorFormProps
  extends Pick<
    NewJobsWizardFormProps,
    | "customerId"
    | "changeCustomerId"
    | "farmMapData"
    | "farmsLoading"
    | "farmIdByFieldId"
  > {
  titleI18nKey: string;
  subtitleI18nKey: string;
  searchPlaceholderI18nKey: string;
  selectionCountI18nKey: string;
  columns: GridColDef<ZoneModel>[];
  checkboxSelection: boolean;
  filterZones: (zone: Zone) => boolean;
  isZoneSelectable?: (zone: Zone) => boolean;
}

export const ZoneSelectorForm: React.FC<ZoneSelectorFormProps> = ({
  titleI18nKey,
  subtitleI18nKey,
  searchPlaceholderI18nKey,
  selectionCountI18nKey,
  customerId,
  changeCustomerId,
  farmMapData,
  farmsLoading,
  farmIdByFieldId,
  columns,
  checkboxSelection,
  filterZones,
  isZoneSelectable,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<NewJobsFormState>();
  const showCustomerSwitcher = useAuthorizationRequired({
    and: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.customers,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.autotractor_jobs,
        PermissionDomain.all
      ),
    ],
  });
  const { data: customers, isLoading: customersLoading } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery(undefined, {
      skip: !showCustomerSwitcher,
    })
  );

  // this step only owns the selected zones it can display (e.g. fields or
  // paths); zones of other kinds selected on other steps are left alone
  const ownsZone = (zoneId: string): boolean => {
    const zone = farmMapData[farmIdByFieldId[zoneId] ?? ""]?.zonesById[zoneId];
    return zone !== undefined && filterZones(zone);
  };
  const selectedZoneIds = values.selectedFieldIds.filter((zoneId) =>
    ownsZone(zoneId)
  );
  const setSelectedZoneIds = (zoneIds: string[]): void => {
    setFieldValue(S.selectedFieldIds.$path, [
      ...values.selectedFieldIds.filter((zoneId) => !ownsZone(zoneId)),
      ...zoneIds,
    ]);
    setFieldTouched(S.selectedFieldIds.$path, true);
  };

  const filteredZoneModels = useMemo(() => {
    const fields: ZoneModel[] = [];

    for (const farm of Object.values(farmMapData)) {
      for (const zone of Object.values(farm.zonesById)) {
        if (!farm.id?.id || !zone.id?.id) {
          continue;
        }

        const display =
          // farm customer matches effective customer id
          farm.customerId.toString() === customerId &&
          // farm selector is "show all farms" or matches
          (values.showFieldsOnFarmId === "" ||
            farm.id.id === values.showFieldsOnFarmId) &&
          // zone name contains search query
          zone.name.toLowerCase().includes(values.fieldSearch.toLowerCase());

        if (display && filterZones(zone)) {
          const pathOrigin = zone.contents?.path?.startZoneId
            ? farm.zonesById[zone.contents.path.startZoneId.id]?.name
            : zone.contents?.path?.startingLocationDescription;

          const pathDestination = zone.contents?.path?.startZoneId
            ? farm.zonesById[zone.contents.path.startZoneId.id]?.name
            : zone.contents?.path?.endingLocationDescription;

          fields.push({
            id: zone.id.id,
            fieldName: zone.name,
            farmName: farm.name,
            pathOrigin,
            pathDestination,
            plantingHeading: getPlantingHeading(zone, farm.pointsById),
            zone,
          });
        }
      }
    }

    return fields;
  }, [
    customerId,
    farmMapData,
    filterZones,
    values.fieldSearch,
    values.showFieldsOnFarmId,
  ]);

  return (
    <>
      <WizardStepButtonBar disableNext={selectedZoneIds.length === 0} />
      <Paper
        variant="outlined"
        className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
      >
        <Typography variant="h5">{t(titleI18nKey)}</Typography>
        <div className="flex flex-row justify-between">
          <Typography color="textSecondary">{t(subtitleI18nKey)}</Typography>
          <Typography>
            {t(selectionCountI18nKey, {
              count: selectedZoneIds.length,
            })}
          </Typography>
        </div>
        <hr className="w-full" />
        <div className="flex flex-row gap-4 items-baseline">
          {showCustomerSwitcher && (
            <MuiSelect
              value={customerId}
              onChange={(e) => changeCustomerId(e.target.value as string)}
              className="min-w-80"
              displayEmpty
            >
              <MenuItem disabled value="">
                <strong>
                  {t(
                    "views.autotractor.jobCreation.step2SelectFields.selectCustomer"
                  )}
                </strong>
              </MenuItem>
              {customers?.map((customer) => {
                if (customer.db?.id) {
                  return (
                    <MenuItem
                      key={customer.db.id}
                      value={customer.db.id.toString()}
                    >
                      {customer.name}
                    </MenuItem>
                  );
                }
              })}
            </MuiSelect>
          )}
          <Field
            component={FormikSelect}
            name={S.showFieldsOnFarmId.$path}
            className="min-w-80"
            displayEmpty
          >
            <MenuItem value="">
              {t(
                "views.autotractor.jobCreation.step2SelectFields.showAllFarms"
              )}
            </MenuItem>
            {Object.values(farmMapData)
              .filter((farm) => farm.customerId.toString() === customerId)
              .map((farm) => {
                if (farm.id?.id) {
                  return (
                    <MenuItem value={farm.id.id} key={farm.id.id}>
                      {farm.name}
                    </MenuItem>
                  );
                }
              })}
          </Field>
          <Field
            component={TextField}
            name={S.fieldSearch.$path}
            placeholder={t(searchPlaceholderI18nKey)}
          ></Field>
        </div>
        {farmsLoading || customersLoading ? (
          <CarbonLoadingSpinner className="justify-center my-4" />
        ) : (
          <Paper variant="outlined" className="bg-inherit rounded-lg">
            <DataGridPremium
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
              rows={filteredZoneModels}
              pageSizeOptions={[10, 20, 50]}
              pagination
              autoHeight
              checkboxSelection={checkboxSelection}
              isRowSelectable={
                isZoneSelectable
                  ? ({ row: { zone } }) => isZoneSelectable(zone)
                  : undefined
              }
              rowSelectionModel={arrayToRowSelectionModel(selectedZoneIds)}
              hideFooterSelectedRowCount
              onRowSelectionModelChange={(model) => {
                setSelectedZoneIds(
                  rowSelectionModelToArray(
                    model,
                    filteredZoneModels
                      .filter(
                        (zoneModel) =>
                          !isZoneSelectable || isZoneSelectable(zoneModel.zone)
                      )
                      .map((zoneModel) => zoneModel.id)
                  ) as string[]
                );
              }}
            />
          </Paper>
        )}
      </Paper>
      <WizardStepButtonBar disableNext={selectedZoneIds.length === 0} />
    </>
  );
};
